import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchValuations,
  fetchValuationById,
  createManualValuation,
  generateAutomaticValuation,
  updateValuationHeader,
  deleteValuation,
  changeValuationState,
  recalculateValuationTotals,
  fetchValuationSummary,
  fetchValuationPdfData,
  fetchEligibleOrders,
  previewEligibleOrders,
  addOrdersToValuation,
  removeOrderFromValuation,
  createValuationDetail,
  updateValuationDetail,
  deleteValuationDetail,
  fetchValuationAdjustments,
  createValuationAdjustment,
  updateValuationAdjustment,
  deleteValuationAdjustment,
  fetchValuationHistory,
  getValuationPdf,
} from '../../api/administration/valuationApi';

export const useValuations = () => {
  const queryClient = useQueryClient();

  const invalidateValuations = async () => {
    await queryClient.invalidateQueries({ queryKey: ['valuations'] });
  };

  const invalidateValuationDetail = async (id) => {
    if (!id) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['valuation', id] }),
      queryClient.invalidateQueries({ queryKey: ['valuation-summary', id] }),
      queryClient.invalidateQueries({ queryKey: ['valuation-pdf-data', id] }),
      queryClient.invalidateQueries({ queryKey: ['valuation-adjustments', id] }),
      queryClient.invalidateQueries({ queryKey: ['valuation-history', id] }),
    ]);
  };

  // =========================
  // MUTATIONS - CABECERA
  // =========================

  const createManualMutation = useMutation({
    mutationFn: createManualValuation,
    onSuccess: async () => {
      await invalidateValuations();
    },
  });

  const generateAutomaticMutation = useMutation({
    mutationFn: generateAutomaticValuation,
    onSuccess: async () => {
      await invalidateValuations();
    },
  });

  const updateHeaderMutation = useMutation({
    mutationFn: ({ id, data }) => updateValuationHeader(id, data),
    onSuccess: async (_, variables) => {
      await invalidateValuations();
      await invalidateValuationDetail(variables?.id);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteValuation,
    onSuccess: async (_, id) => {
      await invalidateValuations();
      await invalidateValuationDetail(id);
    },
  });

  const changeStateMutation = useMutation({
    mutationFn: ({ id, newState }) => changeValuationState(id, newState),
    onSuccess: async (_, variables) => {
      await invalidateValuations();
      await invalidateValuationDetail(variables?.id);
    },
  });

  const recalculateMutation = useMutation({
    mutationFn: recalculateValuationTotals,
    onSuccess: async (_, id) => {
      await invalidateValuations();
      await invalidateValuationDetail(id);
    },
  });

  // =========================
  // MUTATIONS - ÓRDENES
  // =========================

  const addOrdersMutation = useMutation({
    mutationFn: ({ id, orderIds, crebyid }) => addOrdersToValuation(id, orderIds, crebyid),
    onSuccess: async (_, variables) => {
      await invalidateValuations();
      await invalidateValuationDetail(variables?.id);
      queryClient.invalidateQueries({ queryKey: ['eligible-orders'] });
      queryClient.invalidateQueries({ queryKey: ['eligible-orders-preview'] });
    },
  });

  const removeOrderMutation = useMutation({
    mutationFn: ({ id, orderId }) => removeOrderFromValuation(id, orderId),
    onSuccess: async (_, variables) => {
      await invalidateValuations();
      await invalidateValuationDetail(variables?.id);
      queryClient.invalidateQueries({ queryKey: ['eligible-orders'] });
      queryClient.invalidateQueries({ queryKey: ['eligible-orders-preview'] });
    },
  });

  // =========================
  // MUTATIONS - DETALLES
  // =========================

  const createDetailMutation = useMutation({
    mutationFn: ({ id, data }) => createValuationDetail(id, data),
    onSuccess: async (_, variables) => {
      await invalidateValuations();
      await invalidateValuationDetail(variables?.id);
      queryClient.invalidateQueries({ queryKey: ['eligible-orders'] });
      queryClient.invalidateQueries({ queryKey: ['eligible-orders-preview'] });
    },
  });

  const updateDetailMutation = useMutation({
    mutationFn: ({ id, detailId, data }) =>
      updateValuationDetail(id, detailId, data),
    onSuccess: async (_, variables) => {
      await invalidateValuations();
      await invalidateValuationDetail(variables?.id);
      queryClient.invalidateQueries({ queryKey: ['eligible-orders'] });
      queryClient.invalidateQueries({ queryKey: ['eligible-orders-preview'] });
    },
  });

  const deleteDetailMutation = useMutation({
    mutationFn: ({ id, detailId, data }) =>
      deleteValuationDetail(id, detailId, data),
    onSuccess: async (_, variables) => {
      await invalidateValuations();
      await invalidateValuationDetail(variables?.id);
      queryClient.invalidateQueries({ queryKey: ['eligible-orders'] });
      queryClient.invalidateQueries({ queryKey: ['eligible-orders-preview'] });
    },
  });
  // =========================
  // MUTATIONS - PDF
  // =========================
  const printValuationPDF = async (valuationId, options = {}) => {
    if (!valuationId) return null;

    try {
      const blob = await getValuationPdf(valuationId, {
        includeCalendar: options.includeCalendar === true,
      });

      const pdfBlobUrl = URL.createObjectURL(blob);
      return pdfBlobUrl;
    } catch (error) {
      console.error('Error al obtener el PDF de la valorizacion:', error);
      return null;
    }
  };

  // =========================
  // MUTATIONS - AJUSTES
  // =========================

  const addAdjustmentMutation = useMutation({
    mutationFn: ({ id, data }) => createValuationAdjustment(id, data),
    onSuccess: async (_, variables) => {
      await invalidateValuations();
      await invalidateValuationDetail(variables?.id);
    },
  });

  const updateAdjustmentMutation = useMutation({
    mutationFn: ({ id, adjustmentId, data }) =>
      updateValuationAdjustment(id, adjustmentId, data),
    onSuccess: async (_, variables) => {
      await invalidateValuations();
      await invalidateValuationDetail(variables?.id);
    },
  });

  const deleteAdjustmentMutation = useMutation({
    mutationFn: ({ id, adjustmentId }) =>
      deleteValuationAdjustment(id, adjustmentId),
    onSuccess: async (_, variables) => {
      await invalidateValuations();
      await invalidateValuationDetail(variables?.id);
    },
  });

  // =========================
  // QUERIES DINÁMICAS
  // =========================

  const useFetchValuation = (id) => {
    return useQuery({
      queryKey: ['valuation', id],
      queryFn: () => fetchValuationById(id),
      enabled: !!id,
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    });
  };

  const useValuationSummary = (id) => {
    return useQuery({
      queryKey: ['valuation-summary', id],
      queryFn: () => fetchValuationSummary(id),
      enabled: !!id,
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    });
  };

  const useValuationPdfData = (id) => {
    return useQuery({
      queryKey: ['valuation-pdf-data', id],
      queryFn: () => fetchValuationPdfData(id),
      enabled: !!id,
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    });
  };

  const useValuationAdjustments = (id) => {
    return useQuery({
      queryKey: ['valuation-adjustments', id],
      queryFn: () => fetchValuationAdjustments(id),
      enabled: !!id,
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    });
  };

  const useValuationHistory = (id) => {
    return useQuery({
      queryKey: ['valuation-history', id],
      queryFn: () => fetchValuationHistory(id),
      enabled: !!id,
      staleTime: 1000 * 60 * 2,
      refetchOnWindowFocus: false,
    });
  };

  const useEligibleOrders = ({
    proID,
    fecIni,
    fecFin,
    search = '',
    limit = 100,
    offset = 0,
    enabled = true,
  }) => {
    return useQuery({
      queryKey: [
        'eligible-orders',
        proID,
        fecIni,
        fecFin,
        search,
        limit,
        offset,
      ],
      queryFn: () =>
        fetchEligibleOrders({
          proID,
          fecIni,
          fecFin,
          search,
          limit,
          offset,
        }),
      enabled: !!proID && !!fecIni && !!fecFin && enabled,
      staleTime: 1000 * 60 * 2,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    });
  };

  const useEligibleOrdersPreview = ({
    proid,
    fecini,
    fecfin,
    search = '',
    limit = 1000,
    offset = 0,
    enabled = true,
  }) => {
    return useQuery({
      queryKey: [
        'eligible-orders-preview',
        proid,
        fecini,
        fecfin,
        search,
        limit,
        offset,
      ],
      queryFn: () =>
        previewEligibleOrders({
          proid,
          fecini,
          fecfin,
          search,
          limit,
          offset,
        }),
      enabled: !!proid && !!fecini && !!fecfin && enabled,
      staleTime: 1000 * 60 * 2,
      refetchOnWindowFocus: false,
    });
  };

  return {
    createManualMutation,
    generateAutomaticMutation,
    updateHeaderMutation,
    deleteMutation,
    changeStateMutation,
    recalculateMutation,

    addOrdersMutation,
    removeOrderMutation,

    createDetailMutation,
    updateDetailMutation,
    deleteDetailMutation,

    printValuationPDF,

    addAdjustmentMutation,
    updateAdjustmentMutation,
    deleteAdjustmentMutation,

    useFetchValuation,
    useValuationSummary,
    useValuationPdfData,
    useValuationAdjustments,
    useValuationHistory,
    useEligibleOrders,
    useEligibleOrdersPreview,
  };
};

// =========================
// LISTADO PAGINADO
// =========================

export const usePaginatedValuations = ({
  page = 1,
  limit = 10,
  search = '',
  state = '',
  proID = '',
  fecIni = '',
  fecFin = '',
} = {}) => {
  const {
    data = { data: [], total: 0, page: 1, limit: 10 },
    error,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      'valuations',
      page,
      limit,
      search,
      state,
      proID,
      fecIni,
      fecFin,
    ],
    queryFn: () =>
      fetchValuations({
        page,
        limit,
        search,
        state,
        proID,
        fecIni,
        fecFin,
      }),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
  });

  return {
    valuations: data.data || [],
    total: data.total || 0,
    page: data.page || page,
    limit: data.limit || limit,
    error,
    isLoading,
    isFetching,
  };
};